import { usePost } from "@albertine/api-client";
import type { CreatePublicShareLinkRequest } from "@albertine/types/openapi/lmt";
import { isAfter, subHours } from "date-fns";
import { useHistory } from "react-router";
import { serializeError } from "serialize-error";
import type { Booking, ProposalFeedbackEnum } from "../../common/types/Booking";
import type { Request } from "../../common/types/Request";
import {
    findBookingFromProposal,
    groupBookings,
} from "../../common/utils/proposal.util";
import { EventEmitter } from "../../EventEmitter";
import {
    copyCurrentUrlToClipboard,
    copyTextToClipboard,
    getUrlPathComponents,
    logErrorAndShowToast,
    readTextFromClipboard,
} from "../../utils/window.util";
import { MenuButton, type MenuButtonOptions } from "../Common/Button/MenuButton";
import { Flex } from "../Common/Flex";
import Icon, { PictogramIcon } from "../Common/Icon";
import "./Proposal.css";
import { displayReservationStatus } from "../../common/types/BookingReservationStatus";
import { sortArrayByKeyOrder } from "../../common/utils/array.util";
import { emptyImage } from "../../utils/file.util";
import routes from "../../utils/routes";
import { fromTimestampToDate } from "../../utils/timestamp.util";
import Button from "../Common/Buttons";
import { FormattedDateTime, FormattedTime } from "../Common/DateTime";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import Label from "../Common/Label";
import MemberRating from "../Common/rating/MemberRating";
import Tooltip from "../Common/Tooltip";

const feedBack: Record<ProposalFeedbackEnum, React.JSX.Element> = {
    like: (
        <Tooltip label="Member likes this">
            <PictogramIcon.ThumbUp.Medium.P90 />
        </Tooltip>
    ),
    dislike: (
        <Tooltip label="Member disliked this">
            <PictogramIcon.ThumbDown.Medium.E90 />
        </Tooltip>
    ),
};

const feedBackMobile: Record<ProposalFeedbackEnum, React.JSX.Element> = {
    like: (
        <span className="proposal__reaction-mobile">
            <PictogramIcon.ThumbUp.Small.P90 />
        </span>
    ),
    dislike: (
        <span className="proposal__reaction-mobile">
            <PictogramIcon.ThumbDown.Small.E90 />
        </span>
    ),
};

async function pasteBookings(
    toRequest: string,
    copyBookings: (params: {
        param: { id: string };
        json: { fromRequestId: string };
    }) => Promise<null>,
) {
    const lastClipboardText = await readTextFromClipboard();

    if (lastClipboardText) {
        const urlPath = getUrlPathComponents(lastClipboardText);

        if (urlPath && urlPath[0] === "request") {
            const fromRequest = urlPath[1];

            try {
                await copyBookings({
                    param: { id: toRequest },
                    json: { fromRequestId: fromRequest },
                });

                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        type: "info",
                        message: "Bookings copied successfully",
                    },
                });
            } catch (error) {
                const serialized = serializeError(error);
                const message =
                    serialized.status === 409 ||
                    serialized.error ===
                        "Source and destination requests should share same offering category."
                        ? "Source and destination requests should share the same category."
                        : "Copying bookings failed.";
                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        type: "error",
                        message,
                    },
                });
            }
        } else {
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    type: "error",
                    message: "Copy options first from a request.",
                },
            });
        }
    } else {
        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                type: "error",
                message:
                    "Pasting bookings failed. Have you allowed LMT to read clipboard from browser settings?",
            },
        });
    }
}

async function archiveBooking(
    booking: Booking,
    archiveBookingMutation: (args: {
        param: { bookingId: string };
    }) => Promise<unknown>,
) {
    if (!booking) throw new Error("No active booking available!");
    if (!window.confirm(`Delete booking ${booking.title}?`)) {
        return;
    }
    try {
        await archiveBookingMutation({
            param: { bookingId: booking.id },
        });
        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                type: "info",
                message: "Option succesfully deleted",
            },
        });
    } catch {
        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                type: "error",
                message: "Deleting an option failed",
            },
        });
    }
}

function ProposalOption(props: {
    request: Request;
    booking: Booking;
    onClick: (booking: Booking) => void;
}) {
    const { booking, request, onClick } = props;
    const history = useHistory();
    const { mutateAsync: archiveBookingMutation } = usePost("archiveBooking");
    const lastSent = findBookingFromProposal(request, booking.id, "last-send");
    const lastSentWithinADay = lastSent
        ? isAfter(fromTimestampToDate(lastSent.sentAt), subHours(new Date(), 24))
        : false;

    const lastSentLabel = lastSent ? (
        <>
            Sent&nbsp;
            {lastSentWithinADay ? (
                <FormattedTime.Comparison timestamp={lastSent.sentAt} />
            ) : (
                <FormattedDateTime.PrettyTimestamp timestamp={lastSent.sentAt} />
            )}
        </>
    ) : (
        "Not sent"
    );

    const subTitle = booking.reviewNeeded ? (
        "Please review"
    ) : (
        <>
            {displayReservationStatus(booking.reservationStatus)}
            <InlineTextDelimiter />
            {lastSentLabel}
        </>
    );

    const proposalFeedback =
        booking.proposalFeedback && feedBack[booking.proposalFeedback]
            ? feedBack[booking.proposalFeedback]
            : null;

    const proposalFeedbackMobile =
        booking.proposalFeedback && feedBackMobile[booking.proposalFeedback]
            ? feedBackMobile[booking.proposalFeedback]
            : null;

    const scrollChatIntoMessage = () => {
        if (lastSent?.message) {
            EventEmitter.emit({
                name: "scrollChatIntoMessage",
                params: { messageId: lastSent.message },
            });
            history.push(routes.requestWithMessage(request.id, lastSent.message));
        }
    };

    const addBookingToChatAttachments = () => {
        EventEmitter.emit({
            name: "addBookingToChatAttachments",
            params: { booking },
        });
    };

    return (
        <Flex.Row className="proposal__option" gap="medium" alignItems="start">
            <div
                className="proposal__option-image-container"
                onClick={() => {
                    onClick(booking);
                }}
            >
                <img
                    className="proposal__option-image"
                    src={booking.croppedImageURL || booking.imageURL || emptyImage}
                    alt={booking.title || ""}
                />
                {booking.reservationStatus === "confirmed" && (
                    <PictogramIcon.Check.Small.P90 className="proposal__option-icon" />
                )}
                {booking.reviewNeeded && (
                    <PictogramIcon.PriorityHigh.Small.E90 className="proposal__option-icon" />
                )}
                {booking.reviewNeeded && booking.reservationStatus === "confirmed" && (
                    <PictogramIcon.PriorityHigh.Small.S90 className="proposal__option-icon" />
                )}
            </div>

            <Flex.Column
                gap="small"
                onClick={() => {
                    onClick(booking);
                }}
            >
                <h4 className="proposal__option-title">{booking.title}</h4>
                <Label.Small.N60>{subTitle}</Label.Small.N60>

                {booking.bookingRating && (
                    <MemberRating
                        maxRating={5}
                        userRating={booking.bookingRating}
                        message={booking.bookingFeedback}
                    />
                )}
            </Flex.Column>

            {proposalFeedback}

            <div>
                <Flex.Row
                    gap="large"
                    alignItems="center"
                    justifyContent="end"
                    className="proposal__option-actions"
                >
                    {proposalFeedbackMobile}

                    {lastSent?.message && (
                        <Tooltip label="See in chat" placement="top-right">
                            <Button.Ghost
                                onClick={() => {
                                    scrollChatIntoMessage();
                                }}
                            >
                                <Icon.ArrowUpward.Medium.N10 className="proposal__option-action" />
                            </Button.Ghost>
                        </Tooltip>
                    )}

                    {booking.reservationStatus !== "confirmed" &&
                        !lastSent?.message && (
                            <Tooltip label="Delete option" placement="top-right">
                                <Button.Ghost
                                    onClick={() =>
                                        archiveBooking(booking, archiveBookingMutation)
                                    }
                                >
                                    <Icon.Delete.Medium.N10 className="proposal__option-action" />
                                </Button.Ghost>
                            </Tooltip>
                        )}

                    {!booking.reviewNeeded && (
                        <Tooltip label="Add to chat" placement="top-right">
                            <Button.Ghost
                                onClick={() => {
                                    addBookingToChatAttachments();
                                }}
                            >
                                <Icon.ChatPasteGo.Medium.N10 className="proposal__option-action" />
                            </Button.Ghost>
                        </Tooltip>
                    )}
                </Flex.Row>
            </div>
        </Flex.Row>
    );
}

type ProposalProps = {
    request: Request;
    bookings: Booking[];
    onOptionClick: (booking: Booking) => void;
    onAddNewOptionClick: () => void;
};

function Proposal(props: ProposalProps) {
    const { request, bookings, onOptionClick: onClick, onAddNewOptionClick } = props;
    const { mutateAsync: copyRequestBookings } = usePost("copyRequestBookings");
    const { mutateAsync: createShareLink } = usePost("createShareLink");

    const categoryMissing = request && !request.offeringCategory;

    async function getShareUrl(data: CreatePublicShareLinkRequest): Promise<string> {
        try {
            const response = await createShareLink({ json: data });
            return response.url;
        } catch (error) {
            logErrorAndShowToast(error, "Getting link to proposal failed");
            return "";
        }
    }

    const optionsMenuCopy: MenuButtonOptions = [
        {
            label: "Copy options",
            icon: "ContentCopy",
            onClick: () => {
                copyCurrentUrlToClipboard(
                    "Options copied. You can now paste them to another request.",
                );
            },
        },
    ];

    const shareProposalButton: MenuButtonOptions = [
        {
            label: "Share proposal",
            icon: "Link",
            onClick: async () => {
                copyTextToClipboard(
                    await getShareUrl({
                        resourceType: "proposal",
                        resourceId: request.id,
                    }),
                    "Link copied to clipboard. You can now share it.",
                );
            },
        },
    ];

    const confirmedBookingId = bookings.filter(
        (b) => b.reservationStatus === "confirmed",
    )[0]?.id;
    const shareBookingButton: MenuButtonOptions = confirmedBookingId
        ? [
              {
                  label: "Share confirmed booking",
                  icon: "Link",
                  onClick: async () => {
                      copyTextToClipboard(
                          await getShareUrl({
                              resourceType: "booking",
                              resourceId: confirmedBookingId,
                          }),
                          "Link copied to clipboard. You can now share it.",
                      );
                  },
              },
          ]
        : [];

    const optionsMenu: MenuButtonOptions = [
        ...(bookings.length !== 0 ? optionsMenuCopy : []),
        {
            label: "Paste options",
            icon: "ContentPaste",
            onClick: async () => {
                await pasteBookings(request.id, copyRequestBookings);
            },
        },
        ...shareProposalButton,
        ...shareBookingButton,
    ];

    const sortedBookings = sortArrayByKeyOrder(
        request.proposalBookings || [],
        bookings,
        (booking) => booking.id,
    );

    const groupedBookings = groupBookings(request, sortedBookings);

    return (
        <Flex.Column gap="medium">
            <Flex.Row justifyContent="space-between">
                <h5>Options</h5>
                <MenuButton
                    placement="right"
                    options={optionsMenu}
                    button={
                        <Flex.Row className="pictogram-icon--hover">
                            <PictogramIcon.MoreVert.Medium.N99 />
                        </Flex.Row>
                    }
                />
            </Flex.Row>
            <Flex.Column gap="medium" className="proposal">
                {groupedBookings.confirmed.length !== 0 && (
                    <Flex.Column gap="medium">
                        <h6 className="proposal__group-title">Confirmed</h6>

                        <Flex.Column gap="xsmall">
                            {groupedBookings.confirmed.map((booking) => (
                                <ProposalOption
                                    key={booking.id}
                                    booking={booking}
                                    request={request}
                                    onClick={onClick}
                                />
                            ))}
                        </Flex.Column>
                    </Flex.Column>
                )}

                {groupedBookings.sent.length !== 0 && (
                    <Flex.Column gap="medium">
                        <h6 className="proposal__group-title">Proposed to member</h6>

                        <Flex.Column gap="xsmall">
                            {groupedBookings.sent.map((booking) => (
                                <ProposalOption
                                    key={booking.id}
                                    booking={booking}
                                    request={request}
                                    onClick={onClick}
                                />
                            ))}
                        </Flex.Column>
                    </Flex.Column>
                )}
            </Flex.Column>

            <Flex.Column gap="medium">
                {groupedBookings.unsent.length !== 0 && (
                    <h6 className="proposal__group-title">Not visible to member</h6>
                )}

                <Flex.Column gap="xsmall">
                    {groupedBookings.unsent.map((booking) => (
                        <ProposalOption
                            key={booking.id}
                            booking={booking}
                            request={request}
                            onClick={onClick}
                        />
                    ))}

                    <Flex.Row gap="medium" className="proposal__option-add-new">
                        <button
                            type="button"
                            onClick={onAddNewOptionClick}
                            disabled={categoryMissing}
                        >
                            <Flex.Row gap="medium">
                                <Flex.Column
                                    alignItems="center"
                                    justifyContent="center"
                                    className="proposal__option-add-new-icon-container"
                                >
                                    <Icon.Add.Medium.P50 />
                                </Flex.Column>
                                <Tooltip
                                    placement="top-left"
                                    label="Set request category first!"
                                    hide={!categoryMissing}
                                >
                                    <h4>Add new</h4>
                                </Tooltip>
                            </Flex.Row>
                        </button>
                    </Flex.Row>
                </Flex.Column>
            </Flex.Column>
        </Flex.Column>
    );
}

export default Proposal;
